import React, { useCallback, useMemo } from 'react';
import block from 'propmods';
import { RadioButton, CheckBox } from 'lego-on-react';
import * as smartHomeFormKeyset from 'i18n/SmartHomeForm';
import * as languagesKeyset from 'i18n/Language';
import * as regionsKeyset from 'i18n/Region';
import i18n from '@yandex-int/i18n';
import { Field } from '../../Form/Form';
import Caption from 'client/blocks/Form/Caption';
import { SupportedLanguageFormData, RegionSufficientLanguage, RegionCode, LanguageCode } from '../../../types';
import { smartHomeLabels as l } from '../channels/labels';
import { WithTooltip } from '../../WithTooltip/WithTooltip';
import { getIsYangoRequest, shouldMarkAsProvidedByOtherCompany } from '../../../utils';

import './RegionsSelect.scss';
const b = block('RegionsSelect');

const tSmartHome = i18n(smartHomeFormKeyset);
const tRegions = i18n(regionsKeyset);
const tLanguages = i18n(languagesKeyset);

interface Props {
    enableAllAvailableRegions: boolean;
    languages: SupportedLanguageFormData[];
    selectedRegions: RegionCode[];
    disabled: boolean;
    regionsWithSufficientLanguages: RegionSufficientLanguage[];
    setEnableAllAvailableRegions: (value: boolean) => void;
    setSelectedRegions: (newRegions: RegionCode[]) => void;
    errors: string[] | undefined;
}

export const RegionsSelect: React.FC<Props> = ({
    disabled,
    regionsWithSufficientLanguages,
    setEnableAllAvailableRegions,
    enableAllAvailableRegions,
    languages,
    selectedRegions,
    setSelectedRegions,
    errors,
}) => {
    const regions: { code: RegionCode; title: string; languages: LanguageCode[] }[] = useMemo(() => {
        return regionsWithSufficientLanguages
            .map((v) => ({ code: v.regionCode, title: tRegions(v.regionCode), languages: v.sufficientLanguageCodes }))
            .sort((regionA, regionB) => regionA.title.localeCompare(regionB.title));
    }, [regionsWithSufficientLanguages]);

    const selectedRegionsSet = useMemo(() => {
        return new Set(selectedRegions);
    }, [selectedRegions]);

    const { availableRegionsSet } = useMemo(() => {
        const availableRegions = new Set<RegionCode>();

        for (const lang of languages) {
            if (lang.languageCode === undefined) continue;

            for (const regionSufficientLanguage of regionsWithSufficientLanguages) {
                if (regionSufficientLanguage.sufficientLanguageCodes.indexOf(lang.languageCode) >= 0) {
                    availableRegions.add(regionSufficientLanguage.regionCode);
                }
            }
        }

        return { availableRegionsSet: availableRegions };
    }, [regionsWithSufficientLanguages, languages]);

    const onEnableAllAvailableRegionsChange = useCallback(
        (e) => {
            const newValue = e.target.value;
            setEnableAllAvailableRegions(newValue === 'selectAll');
        },
        [setEnableAllAvailableRegions],
    );

    const chageRegionSelectionCallback = useCallback(
        (region: RegionCode, value: boolean) => () => {
            let newSelectedRegions = [...selectedRegions].filter((v) => value || v !== region);

            if (value) {
                const set = new Set(newSelectedRegions);
                set.add(region);
                newSelectedRegions = Array.from(set.values());
            }

            setSelectedRegions(newSelectedRegions);
        },
        [selectedRegions, setSelectedRegions],
    );

    return (
        <Field label={l.selectedRegions} error={errors && errors[0]}>
            <RadioButton
                theme="normal"
                size="s"
                onChange={onEnableAllAvailableRegionsChange}
                value={enableAllAvailableRegions ? 'selectAll' : 'manual'}
                disabled={disabled}
            >
                <RadioButton.Radio value="selectAll">{tSmartHome('select-all-regions')}</RadioButton.Radio>
                <RadioButton.Radio value="manual">{tSmartHome('manuual-regions-select')}</RadioButton.Radio>
            </RadioButton>
            <div {...b()}>
                {regions.map((region) => {
                    const checked =
                        availableRegionsSet.has(region.code) &&
                        (enableAllAvailableRegions || selectedRegionsSet.has(region.code));

                    const asterisk = shouldMarkAsProvidedByOtherCompany(region.code) ? '*' : '';

                    return (
                        <WithTooltip
                            key={region.code}
                            tooltipChildren={tSmartHome('add-languages-to-select-region', {
                                languages: region.languages.map((v) => tLanguages(v)).join(', '),
                            })}
                            disabled={availableRegionsSet.has(region.code)}
                        >
                            <CheckBox
                                {...b()}
                                theme="normal"
                                size="s"
                                tone="default"
                                view="default"
                                text={region.title + asterisk}
                                checked={checked}
                                disabled={
                                    disabled || enableAllAvailableRegions || !availableRegionsSet.has(region.code)
                                }
                                onChange={chageRegionSelectionCallback(region.code, !checked)}
                            />
                        </WithTooltip>
                    );
                })}
            </div>
            <Caption>
                <>
                    <span>* </span>
                    {getIsYangoRequest() ? tSmartHome('service-provider-yandex') : tSmartHome('service-provider-yango')}
                </>
            </Caption>
        </Field>
    );
};
